import { useEffect, useState, useCallback } from "react";
import { RefreshCw, Search, Trash2, Key, Users, Activity, AlertTriangle } from "lucide-react";
import { useToast } from "../../components/ToastProvider";
import { adminRevit } from "../../services/adminApi";

const T = {
  fontDisplay: "'DM Sans', sans-serif",
  fontBody:    "'DM Sans', sans-serif",
  fontMono: "'DM Sans', sans-serif",
  amber:       "#111111",
  amberDark:   "#333333",
  amberBg:     "rgba(0,0,0,0.06)",
  amberBorder: "rgba(0,0,0,0.12)",
  dark:        "#0c1118",
  white:       "#ffffff",
  border:      "#e8ecf0",
  muted:       "#6b7280",
  mutedLight:  "#9ca3af",
};

function roleBadge(role) {
  const colors = {
    admin:   { bg: "rgba(239,68,68,0.1)",   color: "#ef4444",  border: "rgba(239,68,68,0.25)"   },
    manager: { bg: "rgba(99,102,241,0.1)",  color: "#6366f1",  border: "rgba(99,102,241,0.25)"  },
  };
  const s = colors[role] || { bg: "rgba(107,114,128,0.1)", color: "#6b7280", border: "rgba(107,114,128,0.25)" };
  return (
    <span style={{
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: 12,
      background: s.bg,
      color: s.color,
      border: `1px solid ${s.border}`,
      fontFamily: T.fontBody,
      fontSize: 10,
      letterSpacing: "0.1em",
      
    }}>
      {role}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtRelative(iso) {
  if (!iso) return "Never";
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days}d ago`;
  return fmtDate(iso);
}

function KpiCard({ icon: Icon, label, value, accent, loading }) {
  return (
    <div style={{
      background: T.white,
      border: `1px solid ${T.border}`,
      borderRadius: 12,
      padding: "18px 22px",
      display: "flex",
      alignItems: "center",
      gap: 16,
      flex: 1,
      minWidth: 160,
      boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
    }}>
      <div style={{
        width: 44,
        height: 44,
        borderRadius: 10,
        background: `${accent}18`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}>
        <Icon size={20} style={{ color: accent }} />
      </div>
      <div>
        <div style={{ fontFamily: T.fontBody, fontSize: "1.7rem", fontWeight: 700, color: T.dark, lineHeight: 1 }}>
          {loading ? "—" : value}
        </div>
        <div style={{ fontFamily: T.fontBody, fontSize: 10, letterSpacing: "0.1em",  color: T.muted, marginTop: 3 }}>
          {label}
        </div>
      </div>
    </div>
  );
}

export default function AdminRevitPage() {
  const toast = useToast();

  const [stats,   setStats]   = useState(null);
  const [tokens,  setTokens]  = useState([]);
  const [total,   setTotal]   = useState(0);
  const [search,  setSearch]  = useState("");
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);

  const load = useCallback(async (q = search) => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        adminRevit.stats(),
        adminRevit.tokens(q),
      ]);
      setStats(s);
      setTokens(t.items || []);
      setTotal(t.total || 0);
    } catch {
      toast.error("Failed to load Revit data");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(""); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  useEffect(() => {
    const id = setTimeout(() => load(search), 300);
    return () => clearTimeout(id);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  async function revoke(token) {
    if (!window.confirm(`Revoke token "${token.label}" for ${token.user?.name || token.user?.email}? The Revit Add-in using this token will be immediately logged out.`)) return;
    setRevoking(token.id);
    try {
      await adminRevit.revokeToken(token.id);
      toast.success("Token revoked");
      setTokens((prev) => prev.filter((t) => t.id !== token.id));
      setTotal((n) => Math.max(0, n - 1));
      setStats((s) => s ? { ...s, total: Math.max(0, s.total - 1) } : s);
    } catch {
      toast.error("Failed to revoke token");
    } finally {
      setRevoking(null);
    }
  }

  return (
    <div style={{ fontFamily: T.fontBody }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 28 }}>
        <div>
          <h1 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 24, fontWeight: 700, color: "#0c1118", margin: 0, lineHeight: 1.2 }}>
            Revit Management
          </h1>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#6b7280", margin: "4px 0 0" }}>
            Manage Revit Add-in access tokens for Ceed Civil staff
          </p>
        </div>
        <button
          onClick={() => load(search)}
          disabled={loading}
          style={{
            display: "flex", alignItems: "center", gap: 7,
            padding: "9px 16px", borderRadius: 9,
            background: T.white, border: `1px solid ${T.border}`,
            cursor: loading ? "not-allowed" : "pointer",
            fontFamily: T.fontBody, fontSize: 13, fontWeight: 500, color: T.dark,
            opacity: loading ? 0.6 : 1,
          }}
        >
          <RefreshCw size={14} style={{ animation: loading ? "spin .7s linear infinite" : "none" }} />
          Refresh
        </button>
      </div>

      {/* KPI Cards */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 28 }}>
        <KpiCard icon={Key}      label="Total Tokens"      value={stats?.total}           accent={T.amber}    loading={loading} />
        <KpiCard icon={Activity} label="Active (30 days)"  value={stats?.active}          accent="#10b981"    loading={loading} />
        <KpiCard icon={Users}    label="Authorized Users"  value={stats?.authorizedUsers}  accent="#6366f1"    loading={loading} />
      </div>

      {/* Info banner */}
      <div style={{
        background: T.amberBg, border: `1px solid ${T.amberBorder}`,
        borderRadius: 10, padding: "12px 18px",
        display: "flex", alignItems: "center", gap: 12, marginBottom: 24,
        fontFamily: T.fontBody, fontSize: 13, color: "#92400e",
      }}>
        <AlertTriangle size={16} style={{ color: T.amber, flexShrink: 0 }} />
        Tokens are issued only to Ceed Civil staff (admin and manager roles) via the Revit Add-in login.
        Revoking a token immediately logs that session out of the Add-in.
      </div>

      {/* Token Table */}
      <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.05)", overflow: "hidden" }}>
        {/* Table header row */}
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontFamily: T.fontBody, fontSize: "1.15rem", letterSpacing: "0.04em", color: T.dark }}>
            Active Tokens &nbsp;<span style={{ fontFamily: T.fontBody, fontSize: 11, color: T.muted, fontWeight: 400 }}>({total})</span>
          </div>
          <div style={{ position: "relative" }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: T.mutedLight, pointerEvents: "none" }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search user or label…"
              style={{
                paddingLeft: 32, paddingRight: 12, paddingTop: 8, paddingBottom: 8,
                border: `1px solid ${T.border}`, borderRadius: 8,
                fontFamily: T.fontBody, fontSize: 13, color: T.dark,
                background: "#f9fafb", outline: "none", width: 220,
              }}
            />
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f9fafb", borderBottom: `1px solid ${T.border}` }}>
                {["User", "Role", "Token Label", "Last Used", "Created", ""].map((h) => (
                  <th key={h} style={{
                    padding: "10px 16px", textAlign: "left",
                    fontFamily: T.fontBody, fontSize: 10,
                    letterSpacing: "0.1em", 
                    color: T.muted, fontWeight: 500, whiteSpace: "nowrap",
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && tokens.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: "40px 16px", textAlign: "center", color: T.muted, fontFamily: T.fontBody, fontSize: 13 }}>
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && tokens.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: "40px 16px", textAlign: "center", color: T.muted, fontFamily: T.fontBody, fontSize: 13 }}>
                    {search ? "No tokens match your search." : "No active Revit tokens found."}
                  </td>
                </tr>
              )}
              {tokens.map((t, i) => (
                <tr
                  key={t.id}
                  style={{
                    borderBottom: i < tokens.length - 1 ? `1px solid ${T.border}` : "none",
                    background: "transparent",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#f9fafb")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  {/* User */}
                  <td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>
                    <div style={{ fontFamily: T.fontBody, fontSize: 13, fontWeight: 600, color: T.dark }}>
                      {t.user?.name || "—"}
                    </div>
                    <div style={{ fontFamily: T.fontBody, fontSize: 10, color: T.muted, marginTop: 2 }}>
                      {t.user?.email || ""}
                    </div>
                  </td>

                  {/* Role */}
                  <td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>
                    {t.user?.role ? roleBadge(t.user.role) : "—"}
                  </td>

                  {/* Token label */}
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ fontFamily: T.fontBody, fontSize: 13, color: T.dark }}>
                      {t.label || "Revit Add-in"}
                    </div>
                    <div style={{ fontFamily: T.fontBody, fontSize: 10, color: T.mutedLight, marginTop: 2 }}>
                      {t.tokenHint}
                    </div>
                  </td>

                  {/* Last used */}
                  <td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>
                    <span style={{ fontFamily: T.fontBody, fontSize: 13, color: t.lastUsedAt ? T.dark : T.mutedLight }}>
                      {fmtRelative(t.lastUsedAt)}
                    </span>
                  </td>

                  {/* Created */}
                  <td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>
                    <span style={{ fontFamily: T.fontBody, fontSize: 13, color: T.muted }}>
                      {fmtDate(t.createdAt)}
                    </span>
                  </td>

                  {/* Revoke */}
                  <td style={{ padding: "12px 16px", textAlign: "right", whiteSpace: "nowrap" }}>
                    <button
                      onClick={() => revoke(t)}
                      disabled={revoking === t.id}
                      title="Revoke token"
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 5,
                        padding: "6px 12px", borderRadius: 7,
                        background: revoking === t.id ? "#fef2f2" : "transparent",
                        border: "1px solid rgba(239,68,68,0.25)",
                        cursor: revoking === t.id ? "not-allowed" : "pointer",
                        fontFamily: T.fontBody, fontSize: 12, fontWeight: 500,
                        color: "#dc2626",
                        opacity: revoking === t.id ? 0.6 : 1,
                        transition: "background 0.15s, border-color 0.15s",
                      }}
                      onMouseEnter={(e) => { if (revoking !== t.id) e.currentTarget.style.background = "#fef2f2"; }}
                      onMouseLeave={(e) => { if (revoking !== t.id) e.currentTarget.style.background = "transparent"; }}
                    >
                      <Trash2 size={12} />
                      {revoking === t.id ? "Revoking…" : "Revoke"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
